import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  getRouteApi,
  Outlet,
  redirect,
  useParams,
} from "@tanstack/react-router"
import { Flex, IconButton, Input, Text } from "@ui"
import { useState, type FC } from "react"
import { MdClear } from "react-icons/md"

import { superpowersSolutionListQuery } from "../-shared/superpowers.query"
import { queryClient } from "../../../queryClient"
import { SolutionList } from "./$uid/-components/SolutionList"
import { fuzzySearch } from "./-utils/fuzzySearch"

const routeApi = getRouteApi("/_authenticated/superpowers")

const SuperpowersUidLayout: FC = () => {
  const navigate = routeApi.useNavigate()
  const { uid }: { uid?: string } = useParams({ strict: false })
  const { data: solutionList } = useSuspenseQuery(superpowersSolutionListQuery)
  const [searchParam, setSearchParam] = useState("")

  const haystack = solutionList.solutions.map(
    (s) => `${s.name} ${s.solutionUid} ${s.customer.name}`,
  )
  const filteredSolutions = fuzzySearch(
    solutionList.solutions,
    haystack,
    searchParam.trim(),
  )

  const selectSolution = (solutionUid: string): void => {
    void navigate({
      to: "/superpowers/$uid",
      params: { uid: solutionUid },
    })
  }

  const handleSearchChange = (value: string): void => {
    setSearchParam(value)

    const matches = fuzzySearch(solutionList.solutions, haystack, value.trim())
    if (matches.length === 1 && matches[0].solutionUid !== uid) {
      selectSolution(matches[0].solutionUid)
    }
  }

  return (
    <Flex
      flexDirection="column"
      gap="lg"
      padding="lg"
      width="full"
      height="full"
    >
      <Text textStyle="pageTitle">Superpowers</Text>
      <Flex
        gap="lg"
        width="full"
        flex="1"
        minHeight="none"
      >
        <Flex
          flexDirection="column"
          gap="md"
          maxWidth="[224px]"
          width="full"
        >
          <Flex
            gap="xs"
            alignItems="center"
          >
            <Input
              placeholder="Search"
              value={searchParam}
              onChange={(event) => handleSearchChange(event.target.value)}
              flex="1"
            />
            <IconButton
              icon={MdClear}
              aria-label="Clear search"
              onClick={() => setSearchParam("")}
              visibility={searchParam ? "visible" : "hidden"}
            />
          </Flex>
          <SolutionList
            solutions={filteredSolutions}
            selectedUid={uid}
            onSelect={selectSolution}
          />
        </Flex>
        <Flex
          width="full"
          flexDirection="column"
          gap="md"
          overflowY="auto"
          minHeight="none"
        >
          <Outlet />
        </Flex>
      </Flex>
    </Flex>
  )
}

export const Route = createFileRoute("/_authenticated/superpowers")({
  beforeLoad: ({ context }) => {
    if (!context.user.hasSuperpowers) {
      throw redirect({ to: "/base-modules" })
    }
  },
  loader: () => queryClient.ensureQueryData(superpowersSolutionListQuery),

  component: SuperpowersUidLayout,
})
